/**
 * --------------------------------------------------------------------------
 * Ace (v2.0.0): sidebar.js
   Handling Sidebar
*/

import $ from 'jquery'
import bootstrap from 'bootstrap'
import Util from './util'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'aceSidebar'
const VERSION = '2.0.0'
const DATA_KEY = 'ace.sidebar'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const Event = {
  SHOW: `show${EVENT_KEY}`,
  HIDE: `hide${EVENT_KEY}`,
  COLLAPSE: `collapse${EVENT_KEY}`,
  EXPAND: `expand${EVENT_KEY}`,
  SHOWN: `shown${EVENT_KEY}`,
  HIDDEN: `hidden${EVENT_KEY}`,
  COLLAPSED: `collapsed${EVENT_KEY}`,
  EXPANDED: `expanded${EVENT_KEY}`,
  LOAD_DATA_API: `load${EVENT_KEY}${DATA_API_KEY}`,
  CLICK_DATA_API: `click${EVENT_KEY}${DATA_API_KEY}`
}

const Selector = {
  SIDEBAR: '.sidebar',
  DATA_TOGGLE: '[data-toggle="sidebar"]',
  DATA_TOGGLE_MOBILE: '[data-toggle-mobile="sidebar"]'
}

const DefaultType = {
  swipe: 'boolean',
  dismiss: 'boolean',
  backdrop: 'boolean',

  gotoactive: 'boolean',
  subscroll: 'boolean',

  pullup: 'boolean'
}

const Default = {
  swipe: false,
  dismiss: false,
  backdrop: false,

  gotoactive: false,
  subscroll: true,

  pullup: false
}

const ClassName = {
  DESKTOP_HIDE: 'collapsed',
  MOBILE_SHOW: 'sidebar-visible',
  COLLAPSED:	'collapsed',

  TOGGLING: 'toggling',

  INNER_HOVER: 'is-hover',

  BACKDROP: 'sidebar-backdrop'
}

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class Sidebar {
  constructor (element, config) {
    this._hasTransitionEvent = false
    this._hasTransitionEvent2 = false
    this._isTransitioning = false
    this._isBeingDismissed = false

    this._sidebar = element
    this._sidebar.classList.remove(...['d-none', 'd-xl-block'])
    this._inner = this._sidebar.querySelector('.sidebar-inner')
    this._config = this._getConfig(config)
    this._scroller = 	this._sidebar.querySelector('[class*="ace-scroll"]')

    this._pullupEnabled = false

    this._triggerArray = [].slice.call(document.querySelectorAll(
      `[data-toggle="sidebar"][href="#${element.id}"],` +
      `[data-toggle="sidebar"][data-target="#${element.id}"]`
    ))
    this._triggerArrayMobile = [].slice.call(document.querySelectorAll(
      `[data-toggle-mobile="sidebar"][href="#${element.id}"],` +
      `[data-toggle-mobile="sidebar"][data-target="#${element.id}"]`
    ))

    //
    this._collapsed = this._sidebar.classList.contains(ClassName.DESKTOP_HIDE)

    $(this._inner).on('focus', 'input', (e) => {
      if (!this._collapsed) return
      this._inner.classList.add('has-focus')
      $(e.target).one('blur', () => {
        this._inner.classList.remove('has-focus')
      })
    })
    //

    this._handleTriggerEvents()
    this.enableSubmenuToggle()

    if (this._config.pullup) this.enableSubmenuPullup()
    if (this._config.gotoactive) this.scrollToActive()
    if (this._config.backdrop) {
      this._sidebar.classList.add(ClassName.BACKDROP)
    } else if (this._sidebar.classList.contains(ClassName.BACKDROP)) {
      this._config.backdrop = true
    }
  }

  // Getters
  static get VERSION () {
    return VERSION
  }

  static get Default () {
    return Default
  }

  static get DefaultType () {
    return DefaultType
  }

  _handleTriggerEvents () {
    var This = this
    $(this._triggerArray).on('click', function () {
      This.toggle()
    })

    $(this._triggerArrayMobile).on('click', function () {
      This.toggleMobile()
    })
  }

  toggle () {
    if (this._sidebar.classList.contains(ClassName.DESKTOP_HIDE)) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  toggleMobile () {
    if (!this._sidebar.classList.contains(ClassName.MOBILE_SHOW)) {
      this.show()
    } else {
      this.hide()
    }
  }
  /// ////

  expand () {
    if (!this._hasTransitionEvent) this._addTransitionEvent()
    if (this._isTransitioning) return

    const ev = new $.Event(Event.EXPAND)
    $(this._sidebar).trigger(ev)
    if (ev.isDefaultPrevented()) return

    this._isTransitioning = true

    this._sidebar.classList.add(ClassName.TOGGLING)
    this._sidebar.classList.remove(ClassName.DESKTOP_HIDE)

    this._updateTriggerBtns(this._triggerArray, true)

    this._collapsed = false

    if (Util.isReducedMotion()) this._toggleCompleted()// call completion now
  }

  collapse () {
    if (!this._hasTransitionEvent) this._addTransitionEvent()
    if (this._isTransitioning) return

    const ev = new $.Event(Event.COLLAPSE)
    $(this._sidebar).trigger(ev)
    if (ev.isDefaultPrevented()) return

    this._isTransitioning = true

    this._sidebar.classList.add(ClassName.TOGGLING)
    this._sidebar.classList.add(ClassName.DESKTOP_HIDE)

    this._updateTriggerBtns(this._triggerArray, false)

    this._collapsed = true

    if (Util.isReducedMotion()) this._toggleCompleted()// call completion now
  }

  show () {
    if (!this._hasTransitionEvent2) this._addTransitionEvent2()

    const ev = new $.Event(Event.SHOW)
    $(this._sidebar).trigger(ev)
    if (ev.isDefaultPrevented()) return

    this._sidebar.classList.add(ClassName.MOBILE_SHOW)
    this._updateTriggerBtns(this._triggerArrayMobile, true)

    // hide sidebar if clicked outside of it
    if (this._config.dismiss) {
      // document.body.classList.add('overflow-hidden')
      $(this._triggerArrayMobile).css('pointer-events', 'none') // disable this button, because if we click on it, it will hide and then instantly show the sidebar again
      $(document).on('mouseup.sidebar-dismiss', (e) => {
        $(this._triggerArrayMobile).css('pointer-events', '')
        if (!$.contains(this._sidebar, e.target)) { // if clicked outside sidebar
          this._dismiss()
        }
      })
    }

    if (this._config.swipe) this.enableSwipeHide()

    if (Util.isReducedMotion()) this._toggleMobileCompleted()// call completion now

    // in some webkit mobile browsers, sidebar scrolling works but scrollbars are not visible, unless something like this forces it to become visible
    if (this._scroller && !this._scroller.classList.contains('overflow-hidden')) {
      this._scroller.classList.add('overflow-hidden')
      this._scroller.offsetHeight// force redraw
      this._scroller.classList.remove('overflow-hidden')
    }

    // hide body scrollbars
    // if sidebar has backdrop or it's .sidebar-push & fixed
    var scrollbarInfo = Util.getScrollbarInfo()
    if (scrollbarInfo.width === 0 && (this._config.backdrop || (this._sidebar.classList.contains('sidebar-push') && this._sidebar.classList.contains('sidebar-fixed')))) {
      document.body.classList.add('mob-sidebar-body')
    }
  }

  hide () {
    if (!this._hasTransitionEvent2) this._addTransitionEvent2()

    const ev = new $.Event(Event.HIDE)
    $(this._sidebar).trigger(ev)
    if (ev.isDefaultPrevented()) return

    this._sidebar.classList.remove(ClassName.MOBILE_SHOW)
    this._updateTriggerBtns(this._triggerArrayMobile, false)

    //if (this._config.backdrop || this._sidebar.classList.contains('sidebar-push')) {
      document.body.classList.remove('mob-sidebar-body')
    //}

    if (Util.isReducedMotion()) this._toggleMobileCompleted()// call completion now
  }

  _dismiss () {
    this.hide()
    // document.body.classList.remove('overflow-hidden');

    $(document.body).off('.sidebar-dismiss')
    $(document).off('.sidebar-swipe')
  }

  _updateTriggerBtns (btns, expanded = false) {
    for (let i = 0, len = btns.length; i < len; i++) {
      if (expanded) btns[i].classList.remove(ClassName.COLLAPSED)
      else btns[i].classList.add(ClassName.COLLAPSED)

      btns[i].setAttribute('aria-expanded', expanded)
    }
  }

  _toggleCompleted () {
    this._isTransitioning = false
    this._sidebar.classList.remove(ClassName.TOGGLING)

    var expanded = !this._sidebar.classList.contains(ClassName.DESKTOP_HIDE)
    if (expanded) $(this._sidebar).trigger(Event.EXPANDED)
    else $(this._sidebar).trigger(Event.COLLAPSED)

    if (expanded) this._inner.classList.remove(ClassName.INNER_HOVER)
  }

  _toggleMobileCompleted () {
    var shown = this._sidebar.classList.contains(ClassName.MOBILE_SHOW)
    if (shown) $(this._sidebar).trigger(Event.SHOWN)
    else $(this._sidebar).trigger(Event.HIDDEN)
  }

  _addTransitionEvent () {
    if (this._hasTransitionEvent) return
    this._hasTransitionEvent = true

    $(this._sidebar)
      .on('transitionend', (e) => {
        if (e.target !== this._sidebar) return// make sure its not the children triggerring the event!

        this._toggleCompleted()
      })

    // add 'is-hover' class to '.sidebar-inner' when it becomes expanded (i.e. when mouse hovers it)
    var counter = 0
    $(this._inner)
      .on('transitionstart', (e) => {
        // skip on mobile (in which propertyName is 'transform')
        if (e.target !== this._inner || e.originalEvent.propertyName !== 'width') return

        counter++
        if (counter === 1) this._inner.classList.add(ClassName.INNER_HOVER)
        // else if (counter >= 2) {
        //  this.classList.remove(ClassName.INNER_HOVER)
        //  counter = 0
        // }
      })
      .on('transitionend', (e) => {
        // skip on mobile (in which propertyName is transform)
        if (e.target !== this._inner || e.originalEvent.propertyName !== 'width') return
        if (this._inner.clientWidth < 120) {
          // just to make sure we remove the extra class name when not needed
          this._inner.classList.remove(ClassName.INNER_HOVER)
          counter = 0
          // blur input element
          if (document.activeElement.tagName === 'INPUT' && this._inner.contains(document.activeElement)) document.activeElement.blur()
        }
      })
  }

  _addTransitionEvent2 () {
    if (this._hasTransitionEvent2) return
    this._hasTransitionEvent2 = true

    $(this._inner).on('transitionend', (e) => {
      if (e.target !== this._inner || e.originalEvent.propertyName !== 'transform') return

      this._toggleMobileCompleted()
    })
  }

  enableSwipeHide () {
    // swipe to hide sidebar
    var x1 = 0
    var y1 = 0
    var swipeDir = 0
    var aceScroll = this._scroller

    var This = this
    var isRTL = false

    var touchMoveCallback = function (ev) {
      var touches = ev.changedTouches[0]
      var newX = touches.pageX
      var newY = touches.pageY

      if (swipeDir === 0) {
        var diffY = Math.abs(y1 - newY)
        var diffX = Math.abs(x1 - newX)

        if (diffY > diffX) {
          swipeDir = 2// vertical i.e. scroll
          if (aceScroll) aceScroll.classList.remove('overflow-hidden')
          // document.body.classList.remove('overflow-hidden')

          $(document).off('touchmove.sidebar-swipe')
        } else if (diffX > 10) {
          swipeDir = 1// horizontal swipe
          This._inner.setAttribute('style', 'transition: none !important; will-change: transform; touch-action: none;')
          if (aceScroll) aceScroll.classList.add('overflow-hidden')
          // document.body.classList.add('overflow-hidden')
        }
      }
      if (swipeDir !== 1) return

      var moveX = parseInt(x1 - newX)
      if ((!isRTL && moveX > 0) || (isRTL && moveX < 0)) { // move it outside of view
        This._inner.style.transform = 'translateX(' + (-1 * moveX) + 'px)'
      } else This._inner.style.transform = ''
    }

    var t1 = 0
    $(document)
      .on('touchstart.sidebar-swipe', function (e) {
        var touches = e.originalEvent.changedTouches[0]
        x1 = touches.pageX
        y1 = touches.pageY

        t1 = Date.now()
        isRTL = document.documentElement.classList.contains('rtl')

        $(this).on('touchmove.sidebar-swipe', function (e) {
          touchMoveCallback(e.originalEvent)
        })
      })
      .on('touchend.sidebar-swipe touchcancel.sidebar-swipe', function (e) {
        var touches = e.originalEvent.changedTouches[0]
        var x2 = touches.pageX
        var t2 = Date.now()

        if (swipeDir === 1 &&
            ( // dismiss if moved by more than 100px or moved more than 40px in a short time (less than 300ms)
              (!isRTL && (x1 - x2 > 100 || (x1 - x2 > 40 && t2 - t1 < 300))) || (isRTL && (x1 - x2 < -100 || (x1 - x2 < -40 && t2 - t1 < 300)))
            )
        ) { // if moved more than 100px or 40px in less than 300ms
          This._dismiss()
        }

        This._inner.setAttribute('style', '')
        if (aceScroll) aceScroll.classList.remove('overflow-hidden')
        // document.body.classList.remove('overflow-hidden')

        swipeDir = 0
      })
  }

  enableSubmenuToggle () {
    var _firefox = 'MozAppearance' in document.documentElement.style
    var _scrollIntoViewOptions = 'scrollBehavior' in document.documentElement.style

    var This = this
    $(this._sidebar).on('click', '.dropdown-toggle', function (ev) {
      ev.preventDefault()

      var navItem = $(this).closest('.nav-item')// get the parent LI.nav-item
      var subMenu = navItem.find('> .submenu').eq(0)// get the direct submenu (not the children)
      if (subMenu.hasClass('collapsing')) return// don't toggle in the middle of toggling

      // hide sibling submenus
      navItem.addClass('is-toggling')
      navItem.parent().find('> .nav-item.open').addClass('is-toggling').not(navItem).removeClass('open').find('> .submenu.show').collapse('hide')

      // toggle submenu
      navItem.toggleClass('open')
      subMenu.collapse('toggle')

      // scroll submenu into view
      if (This._config.subscroll && This._sidebar.classList.contains('sidebar-fixed') && navItem.hasClass('open')) {
        var smooth = !Util.isReducedMotion()
        setTimeout(function () {
          try {
            if (_scrollIntoViewOptions) {
              subMenu.get(0).scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'nearest'
              })
            } else {
              subMenu.get(0).scrollIntoView(false)
            }
          } catch (err) {}
        }, smooth ? 150 : 0)
      }

      subMenu.on('shown.bs.collapse.is-toggling hidden.bs.collapse.is-toggling', function () {
        subMenu.off('.is-toggling')
        $('.nav-item.is-toggling').removeClass('is-toggling')
      })

      // firefox continues to disable scroll chaining when sidebar is not scrollable anymore, so a little fix around here
      if (_firefox) {
        subMenu.one('shown.bs.collapse.ff-fix hidden.bs.collapse.ff-fix', () => {
          subMenu.off('.ff-fix')
          if (This._scroller === null) return
          if (This._scroller.scrollHeight <= This._scroller.clientHeight) {
            This._scroller.style.overscrollBehavior = 'auto'
          } else This._scroller.style.overscrollBehavior = ''
        })
      }
    })
  }

  enableSubmenuPullup () {
    if (this._pullupEnabled) return
    this._pullupEnabled = true

    var This = this
    $(this._sidebar).on('transitionstart', '.submenu', function (ev) {
      if (ev.target !== this || ev.originalEvent.propertyName !== 'margin-left' || !This._sidebar.classList.contains('collapsed')) return

      var navItem = $(this).parent()
      var submenu = $(this)

      var navtxt = navItem.find('> .nav-link > .nav-text.fadeable')// only first level nav-text items

      navItem.removeClass('submenu-pullup')
      submenu.css('transform', '')
      navtxt.css('transform', '')

      /// ///////////////////////

      var rect = submenu.get(0).getBoundingClientRect()
      var wh = $(window).height()

      var diff = parseInt(rect.bottom - wh)

      if (diff > 0) { // if submenu bottom is below window area
        // check to see if submenu top will go out of window if we move it up by "diff" pixels
        // also consider that the first level item's .nav-text shouldn't go out of window's top
        var navtxtHeight = navtxt.height() || 0

        var diff2 = rect.top - navtxtHeight - diff - $('.navbar').height()// don't go above navbar
        if (diff2 < 0) diff = diff + diff2

        diff = parseInt(diff) + 1// so that submenu's border is visible
        if (navtxtHeight && diff > navtxtHeight / 2) navItem.addClass('submenu-pullup')// this class makes the .sub-arrow's color white, to match submenu color

        submenu.css('transform', 'translateY(-' + diff + 'px)')
        navtxt.css('transform', 'translateY(-' + diff + 'px)')
      }
    })
  }

  disableSubmenuPullup () {
    this._pullupEnabled = false
    $(this._sidebar).off('transitionstart.pullup').find('.nav-text, .submenu').css('transform', '')
  }

  // scroll active item into view
  scrollToActive () {
    if (!this._sidebar.classList.contains('sidebar-fixed') || this._scroller === null) return

    var active = this._sidebar.querySelector('.nav-item.active:not(.open) > .nav-link')
    try {
      active.scrollIntoView({ behavior: 'auto', block: 'end' })// or block: "center"?
      this._scroller.scrollTop = this._scroller.scrollTop + 30
    } catch (e) {}
  }

  _getConfig (config) {
    config = {
      ...Default,
      ...typeof config === 'object' && config ? config : {}
    }

    if (typeof bootstrap !== 'undefined') {
      bootstrap.Util.typeCheckConfig(
        NAME,
        config,
        this.constructor.DefaultType
      )
    }

    return config
  }

  // Static methods
  static _jQueryInterface (config) {
    return this.each(function () {
      const $this = $(this)
      let data = $this.data(DATA_KEY)
      const _config = {
        ...Default,
        ...$this.data(),
        ...typeof config === 'object' && config ? config : {}
      }

      if (!data) {
        data = new Sidebar(this, _config)
        $this.data(DATA_KEY, data)
      }

      if (typeof config === 'string') {
        if (typeof data[config] === 'undefined') {
          throw new TypeError(`No method named "${config}"`)
        }
        data[config]()
      }
    })
  }
}

/**
 * ------------------------------------------------------------------------
 * Data Api implementation
 * ------------------------------------------------------------------------
*/

$(window).on(Event.LOAD_DATA_API, () => {
  const sidebars = [].slice.call(document.querySelectorAll(Selector.SIDEBAR))

  for (let i = 0; i < sidebars.length; i++) {
    const $sidebar = $(sidebars[i])
    Sidebar._jQueryInterface.call($sidebar, $sidebar.data())
  }
})

/**
$(document).on(Event.CLICK_DATA_API, `${Selector.DATA_TOGGLE} , ${Selector.DATA_TOGGLE_MOBILE}`, function (event) {
	if (event.currentTarget.tagName === 'A') {
	  event.preventDefault()
	}

	const $trigger = $(this)
	const selector = bootstrap.Util.getSelectorFromElement(this)
	const selectors = [].slice.call(document.querySelectorAll(selector))

	$(selectors).each(function () {
	  const $target = $(this)
	  const data    = $target.data(DATA_KEY)
	  const config  = data ? 'toggle' : $trigger.data()
	  Sidebar._jQueryInterface.call($target, config)
	})
})
*/

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
*/

if (typeof $ !== 'undefined') {
  const JQUERY_NO_CONFLICT = $.fn[NAME]
  $.fn[NAME] = Sidebar._jQueryInterface
  $.fn[NAME].Constructor = Sidebar
  $.fn[NAME].noConflict = () => {
    $.fn[NAME] = JQUERY_NO_CONFLICT
    return Sidebar._jQueryInterface
  }
}

export default Sidebar
